import { keepPreviousData, queryOptions } from '@tanstack/react-query'

import { apiClient } from './client'
import type {
  CollectionAccountHold,
  CollectionAccountHoldPayload,
  CollectionAllocationLine,
  CollectionAllocationLinePayload,
  CollectionLoanAccount,
  CollectionLoanAccountPayload,
  CollectionLoanCycle,
  CollectionLoanCyclePayload,
  CollectionRepaymentAllocation,
  CollectionRepaymentAllocationPayload,
  CollectionRepaymentPosted,
  CollectionRepaymentPostedPayload,
  CollectionRepaymentRequest,
  CollectionSavingsAccount,
  CollectionSavingsAccountPayload,
  CollectionSavingsMovementRequest,
  CollectionSavingsPosting,
  CollectionSavingsPostingPayload,
  CollectionSavingsTransaction,
  CollectionSavingsTransactionPayload,
  LoanAccountStatus,
  SavingsAccountStatus,
  SavingsProductClass,
  WireInteger,
} from './collections-types'
import type { PagedResult } from './types'

/**
 * The reads and writes behind collection capture.
 *
 * There is no collections module on the server and no batch endpoint. A sheet is assembled from
 * three contracts — the group's meeting calendar, the group's loan rounds, each member's savings
 * accounts — and it is posted one line at a time through the endpoints that own the money. That is
 * the honest shape of it, and the screens say so rather than presenting a batch that atomically
 * commits, which this API cannot do. See the area's gaps note.
 *
 * The group reads themselves are not repeated here. `@/api/groups-api` already owns
 * `/groups`, `/groups/{id}` and `/groups/{id}/meetings`, and the collection screens use the query
 * options in `@/features/groups/queries` so that a group opened from a sheet and the same group
 * opened from the group list share one cache entry rather than two that can disagree.
 */

// ------------------------------------------------------------------------------- normalisation

/**
 * An integer as this API may send it — a JSON number or the same digits as a string — reduced to a
 * number, exactly as `reference-api.ts` reduces a branch identifier to a string.
 *
 * Not a nicety on this screen. Amounts are summed into a batch total the operator checks against
 * the cash in front of them, and `"1500" + "1500"` is `"15001500"`. An unparseable value becomes 0
 * rather than `NaN`: a total is either right or visibly short, never the word "NaN" beside a
 * confirm button.
 */
function toInteger(value: WireInteger | null | undefined): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.trunc(value) : 0
  }

  if (typeof value === 'string') {
    // Base 10 explicitly, and never a float parse: these are integer minor units.
    const parsed = Number.parseInt(value, 10)
    return Number.isFinite(parsed) ? parsed : 0
  }

  return 0
}

function toLoanCycle(payload: CollectionLoanCyclePayload): CollectionLoanCycle {
  return {
    id: payload.id,
    cycleNumber: toInteger(payload.cycleNumber),
    displayReference: payload.displayReference,
    status: payload.status,
    principalMinor: toInteger(payload.principalMinor),
    currencyCode: payload.currencyCode,
    disbursementDate: payload.disbursementDate,
    maturityDate: payload.maturityDate,
    principalOutstandingMinor: toInteger(payload.principalOutstandingMinor),
    interestOutstandingMinor: toInteger(payload.interestOutstandingMinor),
    penaltyOutstandingMinor: toInteger(payload.penaltyOutstandingMinor),
    feesOutstandingMinor: toInteger(payload.feesOutstandingMinor),
    totalOutstandingMinor: toInteger(payload.totalOutstandingMinor),
    daysInArrears: toInteger(payload.daysInArrears),
    amountInArrearsMinor: toInteger(payload.amountInArrearsMinor),
    classificationCode: payload.classificationCode,
    isRestructured: payload.isRestructured,
  }
}

function toLoanAccount(payload: CollectionLoanAccountPayload): CollectionLoanAccount {
  return {
    id: payload.id,
    accountNumber: payload.accountNumber,
    customerId: payload.customerId,
    customerName: payload.customerName,
    groupId: payload.groupId,
    branchId: payload.branchId,
    productId: payload.productId,
    productName: payload.productName,
    status: payload.status,
    openedDate: payload.openedDate,
    totalCyclesCompleted: toInteger(payload.totalCyclesCompleted),
    currentCycle: payload.currentCycle === null ? null : toLoanCycle(payload.currentCycle),
  }
}

function toHold(payload: CollectionAccountHoldPayload): CollectionAccountHold {
  return {
    id: payload.id,
    amountMinor: toInteger(payload.amountMinor),
    currencyCode: payload.currencyCode,
    reason: payload.reason,
    note: payload.note,
    placedAtUtc: payload.placedAtUtc,
    releasedAtUtc: payload.releasedAtUtc,
    isActive: payload.isActive,
  }
}

function toSavingsAccount(payload: CollectionSavingsAccountPayload): CollectionSavingsAccount {
  return {
    id: payload.id,
    accountNumber: payload.accountNumber,
    customerId: payload.customerId,
    customerNumber: payload.customerNumber,
    customerName: payload.customerName,
    productId: payload.productId,
    productCode: payload.productCode,
    productClass: payload.productClass,
    branchId: payload.branchId,
    status: payload.status,
    openedDate: payload.openedDate,
    closedDate: payload.closedDate,
    ledgerBalanceMinor: toInteger(payload.ledgerBalanceMinor),
    holdAmountMinor: toInteger(payload.holdAmountMinor),
    availableBalanceMinor: toInteger(payload.availableBalanceMinor),
    currencyCode: payload.currencyCode,
    passbookNumber: payload.passbookNumber,
    lastTransactionDate: payload.lastTransactionDate,
    accruedInterestMinor: toInteger(payload.accruedInterestMinor),
    isWithdrawable: payload.isWithdrawable,
    // Defended rather than trusted: the schema says the array is always sent, and a panel behind
    // the whole sheet must not fail to render because one account came back without it.
    holds: Array.isArray(payload.holds) ? payload.holds.map(toHold) : [],
  }
}

function toSavingsTransaction(
  payload: CollectionSavingsTransactionPayload,
): CollectionSavingsTransaction {
  return {
    id: payload.id,
    transactionNumber: payload.transactionNumber,
    type: payload.type,
    businessDate: payload.businessDate,
    valueDate: payload.valueDate,
    amountMinor: toInteger(payload.amountMinor),
    currencyCode: payload.currencyCode,
    balanceAfterMinor: toInteger(payload.balanceAfterMinor),
    narration: payload.narration,
    postedAtUtc: payload.postedAtUtc,
    postedBy: payload.postedBy,
    journalEntryNumber: payload.journalEntryNumber,
    isReversed: payload.isReversed,
  }
}

function toSavingsPosting(payload: CollectionSavingsPostingPayload): CollectionSavingsPosting {
  return {
    transaction: toSavingsTransaction(payload.transaction),
    balanceAfterMinor: toInteger(payload.balanceAfterMinor),
    availableBalanceMinor: toInteger(payload.availableBalanceMinor),
    currencyCode: payload.currencyCode,
    wasReplay: payload.wasReplay,
  }
}

function toAllocationLine(payload: CollectionAllocationLinePayload): CollectionAllocationLine {
  return {
    instalmentNumber: toInteger(payload.instalmentNumber),
    bucket: payload.bucket,
    amountMinor: toInteger(payload.amountMinor),
  }
}

function toAllocation(
  payload: CollectionRepaymentAllocationPayload,
): CollectionRepaymentAllocation {
  return {
    penaltyMinor: toInteger(payload.penaltyMinor),
    feeMinor: toInteger(payload.feeMinor),
    interestMinor: toInteger(payload.interestMinor),
    principalMinor: toInteger(payload.principalMinor),
    lines: Array.isArray(payload.lines) ? payload.lines.map(toAllocationLine) : [],
  }
}

function toRepaymentPosted(payload: CollectionRepaymentPostedPayload): CollectionRepaymentPosted {
  return {
    transactionId: payload.transactionId,
    cycleId: payload.cycleId,
    amountMinor: toInteger(payload.amountMinor),
    currencyCode: payload.currencyCode,
    valueDate: payload.valueDate,
    allocation: toAllocation(payload.allocation),
    journalEntry: payload.journalEntry,
    cycleClosed: payload.cycleClosed,
    cycle: toLoanCycle(payload.cycle),
  }
}

/**
 * The paging envelope as sent. Its four counts carry the same `["integer", "string"]` union every
 * other integer on this wire does, so they are normalised alongside the items rather than trusted
 * — a table that reads `totalCount` as `"120"` pages through a string.
 */
interface WirePage<TItem> {
  items: TItem[]
  page: WireInteger
  pageSize: WireInteger
  totalCount: WireInteger
  totalPages: WireInteger
}

function toPage<TPayload, TItem>(
  payload: WirePage<TPayload>,
  map: (item: TPayload) => TItem,
): PagedResult<TItem> {
  return {
    items: Array.isArray(payload?.items) ? payload.items.map(map) : [],
    page: toInteger(payload?.page),
    pageSize: toInteger(payload?.pageSize),
    totalCount: toInteger(payload?.totalCount),
    totalPages: toInteger(payload?.totalPages),
  }
}

// -------------------------------------------------------------------------------------- reads

export interface CollectionLoanAccountQuery {
  customerId?: string
  groupId?: string
  branchId?: string
  status?: LoanAccountStatus
  search?: string
  page?: number
  pageSize?: number
}

/** `GET /loan-accounts`. Gated on `loans.view`; a caller without it gets a 403, not an empty page. */
export async function listCollectionLoanAccounts(
  query: CollectionLoanAccountQuery,
): Promise<PagedResult<CollectionLoanAccount>> {
  const response = await apiClient.get<WirePage<CollectionLoanAccountPayload>>('/loan-accounts', {
    params: query,
  })

  return toPage(response.data, toLoanAccount)
}

export interface CollectionSavingsAccountQuery {
  customerId?: string
  status?: SavingsAccountStatus
  productClass?: SavingsProductClass
  search?: string
  branchId?: string
  page?: number
  pageSize?: number
}

/** `GET /savings/accounts`. Gated on `savings.view`. */
export async function listCollectionSavingsAccounts(
  query: CollectionSavingsAccountQuery,
): Promise<PagedResult<CollectionSavingsAccount>> {
  const response = await apiClient.get<WirePage<CollectionSavingsAccountPayload>>(
    '/savings/accounts',
    { params: query },
  )

  return toPage(response.data, toSavingsAccount)
}

// ------------------------------------------------------------------------------------- writes

/**
 * `POST /loan-cycles/{id}/repayments`, under `loans.repayment.post`.
 *
 * Not dual-controlled, and deliberately so: money coming in creates no exposure, and a
 * countersignature on every line of every sheet would stop the collection rather than control it.
 * The control that matters here is custody and the audit trail, not a second signature — so this
 * answers with the posting itself and never with a `DualControlResult`.
 */
export async function postCollectionRepayment(
  cycleId: string,
  request: CollectionRepaymentRequest,
): Promise<CollectionRepaymentPosted> {
  const response = await apiClient.post<CollectionRepaymentPostedPayload>(
    `/loan-cycles/${encodeURIComponent(cycleId)}/repayments`,
    request,
  )

  return toRepaymentPosted(response.data)
}

/** `POST /savings/accounts/{id}/deposits`, under `savings.deposit.post`. Not dual-controlled. */
export async function postCollectionSavingsDeposit(
  accountId: string,
  request: CollectionSavingsMovementRequest,
): Promise<CollectionSavingsPosting> {
  const response = await apiClient.post<CollectionSavingsPostingPayload>(
    `/savings/accounts/${encodeURIComponent(accountId)}/deposits`,
    request,
  )

  return toSavingsPosting(response.data)
}

/**
 * `POST /savings/accounts/{id}/security-deposit`, under `savings.security_deposit.topup`.
 *
 * Its own endpoint and its own permission, not a flag on a deposit: cash collateral is a different
 * act from savings, and a field officer who may take one at a meeting must not be able to take the
 * other there.
 */
export async function postCollectionSecurityDepositTopUp(
  accountId: string,
  request: CollectionSavingsMovementRequest,
): Promise<CollectionSavingsPosting> {
  const response = await apiClient.post<CollectionSavingsPostingPayload>(
    `/savings/accounts/${encodeURIComponent(accountId)}/security-deposit`,
    request,
  )

  return toSavingsPosting(response.data)
}

// ------------------------------------------------------------------------------- query options

/**
 * The loan rounds written under one group.
 *
 * Asked for by group rather than per member, because one request answers the whole sheet. Two
 * hundred is above any group the domain permits — a village bank is a few dozen people — so a
 * second page would mean something is wrong rather than that paging is needed.
 *
 * Deliberately *not* filtered to `status: 'Active'` accounts. `PostRepaymentHandler` refuses on the
 * round's status (`loans.cycle_not_active`) and never on the account's, so a dormant account
 * carrying a live round takes a repayment perfectly well. Filtering those out server-side would
 * drop the member's account from the answer entirely, and the sheet — which cannot tell "filtered
 * out" from "never existed" — would tell an officer the member has no loan under this group. The
 * member would go home unpaid over a filter. Account status is carried on the record and read on
 * the row instead.
 */
export const groupLoanAccountsQueryOptions = (groupId: string, enabled: boolean) =>
  queryOptions({
    queryKey: ['collections', 'loan-accounts', groupId] as const,
    queryFn: () => listCollectionLoanAccounts({ groupId, page: 1, pageSize: 200 }),
    enabled: enabled && groupId !== '',
    // Balances move as the batch posts. Nothing here may be served from a cache filled before the
    // last line landed, because the ceiling on the next repayment is one of these figures.
    staleTime: 0,
    placeholderData: keepPreviousData,
  })

/**
 * One member's savings and security-deposit accounts.
 *
 * Per member rather than per group, because the savings list has no group filter — the same shape
 * of gap the group members panel works around for KYC. The queries share one cache key each, so a
 * member opened afterwards costs nothing.
 */
export const customerSavingsAccountsQueryOptions = (customerId: string, enabled: boolean) =>
  queryOptions({
    queryKey: ['collections', 'savings-accounts', customerId] as const,
    queryFn: () => listCollectionSavingsAccounts({ customerId, page: 1, pageSize: 50 }),
    enabled: enabled && customerId !== '',
    staleTime: 0,
  })
